import {
  type HeadersFunction,
  json,
  type LinksFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import {
  Form,
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useMatch,
  useRouteLoaderData,
  useSubmit,
} from "@remix-run/react";
import { withSentry } from "@sentry/remix";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ExternalScripts } from "remix-utils/external-scripts";
import { HoneypotProvider } from "remix-utils/honeypot/react";
import { toast } from "sonner";
import { clearClientAuthStateOnLogout } from "#app/utils/auth.client";
import appleTouchIconAssetUrl from "./assets/favicons/apple-touch-icon.png";
import faviconAssetUrl from "./assets/favicons/favicon.ico";
import { GeneralErrorBoundary } from "./components/error-boundary.tsx";
import { EpicProgress } from "./components/progress-bar.tsx";
import { PwaManager } from "./components/pwa.tsx";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "./components/ui/avatar.tsx";
import { Button } from "./components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu.tsx";
import { href as iconsHref, Icon } from "./components/ui/icon.tsx";
import { EpicToaster } from "./components/ui/sonner.tsx";
import { OrgTeamProvider } from "./contexts/org-team-context.tsx";
import { type AppLocale } from "./i18n/i18n.ts";
import { useTheme } from "./routes/resources+/theme-switch.tsx";
// import interFontStyleSheetUrl from './styles/inter-font.css?url'
import atmosphericGlassStyles from "./styles/atmospheric-glass.css?url";
import tailwindStyleSheetUrl from "./styles/tailwind.css?url";
import { getUserId, logout } from "./utils/auth.server.ts";
import { ClientHintCheck, getHints } from "./utils/client-hints.tsx";
import { ProviderConnectionForm } from "./utils/connections.tsx";
import { prisma } from "./utils/db.server.ts";
import { getEnv } from "./utils/env.server.ts";
import { honeypot } from "./utils/honeypot.server.ts";
import { getKeycloakClaims } from "./utils/keycloak-claims.server.ts";
import { combineHeaders, getUserImgSrc } from "./utils/misc.tsx";
import { useNonce } from "./utils/nonce-provider.ts";
import { orgTeamSessionStorage } from "./utils/org-team-session.server.ts";
import { getThemeBootstrapScript } from "./utils/theme-bootstrap.ts";
import { getTheme, type Theme } from "./utils/theme.server.ts";
import { makeTimings, time } from "./utils/timing.server.ts";
import { getToast } from "./utils/toast.server.ts";
import { useOptionalUser, useUser } from "./utils/user.ts";

export const handle = {
  i18n: ["common", "marketing"],
};

export const links: LinksFunction = () => {
  return [
    // {
    // 	rel: 'icon',
    // 	sizes: '48x48',
    // 	href: '/favicon.ico',
    // },
    { rel: "manifest", href: "/site.webmanifest" },
    { rel: "stylesheet", href: tailwindStyleSheetUrl },
    // } as const, // necessary to make typescript happy
    { rel: "stylesheet", href: atmosphericGlassStyles },
    // { rel: 'stylesheet', href: interFontStyleSheetUrl },
    // Preload svg sprite as a resource to avoid render blocking
    { rel: "preload", href: iconsHref, as: "image" },
    // { rel: 'preload', href: interFontStyleSheetUrl, as: 'style' },

    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    {
      rel: "preconnect",
      href: "https://fonts.gstatic.com",
      crossOrigin: "anonymous" as any,
    },
    // {
    // 	rel: 'stylesheet',
    // 	href: 'https://fonts.googleapis.com/css2?family=Indie+Flower&display=swap',
    // },
    {
      rel: "stylesheet",
      // href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
      href: "https://fonts.googleapis.com/css2?family=Funnel+Display:wght@300..800&display=swap",
    },
    {
      // Typeface of the new design. Variable weight axis so the sidebar,
      // headings and body all come from one file.
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Google+Sans+Flex:wght@100..1000&display=swap",
    },
    {
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:ital,wght@0,100..800;1,100..800&display=swap",
    },

    {
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;600;700&display=swap",
    },

    { rel: "icon", type: "image/svg+xml", href: faviconAssetUrl },
    { rel: "apple-touch-icon", href: appleTouchIconAssetUrl },
  ].filter(Boolean);
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const title =
    data?.locale === "ja"
      ? "Supervity Autos | AIロールを雇用して業務を実行"
      : "Supervity Autos | Hire AI Roles to Execute Your Work";
  const description =
    data?.locale === "ja"
      ? "ワークフローの構築をやめ、成果の指示に集中しましょう。ロールベースのAIワークフォース「Supervity Autos」のウェイトリストに参加してください。"
      : "Stop building workflows and start directing outcomes. Join the waitlist for the world's first role-based AI workforce with Supervity Autos.";
  return [
    { title: data ? title : "Error | Supervity AI" },
    { name: "description", content: description },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings("root loader");
  const { getRequestLocale } =
    await import("./i18n/get-request-locale.server.ts");
  const locale = await getRequestLocale(request);
  const userId = await time(() => getUserId(request), {
    timings,
    type: "getUserId",
    desc: "getUserId in root",
  });
  const user = userId
    ? await time(
        () =>
          prisma.user.findUniqueOrThrow({
            select: {
              id: true,
              name: true,
              username: true,
              email: true,
              locale: true,
              image: { select: { id: true } },
              roles: {
                select: {
                  name: true,
                  permissions: {
                    select: {
                      entity: true,
                      action: true,
                      access: true,
                    },
                  },
                },
              },
            },
            where: { id: userId },
          }),
        { timings, type: "find user", desc: "find user in root" },
      )
    : null;
  if (userId && !user) {
    console.info("something weird happened");
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: "/" });
  }
  const { toast, headers: toastHeaders } = await getToast(request);
  const theme = getTheme(request);
  const hints = getHints(request);

  // Get org team session to ensure it's included in headers
  const orgTeamSession = await orgTeamSessionStorage.getSession(
    request.headers.get("Cookie"),
  );
  const orgTeamHeaders = new Headers();
  orgTeamHeaders.append(
    "Set-Cookie",
    await orgTeamSessionStorage.commitSession(orgTeamSession),
  );

  // Get honeypot input props
  const honeyProps = honeypot.getInputProps();

  // Keycloak claims (groups, roles) from access token for nav/guards
  const keycloakClaims = userId
    ? await time(() => getKeycloakClaims(request), {
        timings,
        type: "getKeycloakClaims",
        desc: "getKeycloakClaims in root",
      })
    : null;

  return json(
    {
      user,
      locale,
      keycloakClaims,
      requestInfo: {
        // ThemeSwitch posts this as `redirectTo` for the no-JS / early-click
        // fallback. Keep the query string so /login?redirectTo=…,
        // /marketplace?tab=…, /blog?q=… survive a theme toggle.
        path: (() => {
          const url = new URL(request.url);
          return `${url.pathname}${url.search}`;
        })(),
        hints,
        theme,
        userPrefs: {
          theme,
        },
      },
      ENV: getEnv(),
      toast,
      honeyProps,
    },
    {
      headers: combineHeaders(
        { "Server-Timing": timings.toString() },
        toastHeaders,
        orgTeamHeaders,
      ),
    },
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    "Server-Timing": loaderHeaders.get("Server-Timing") ?? "",
  };
  return headers;
};

function Document({
  children,
  nonce,
  theme = "light",
  env = {},
  allowIndexing = true,
  locale = "en",
}: {
  children: React.ReactNode;
  nonce: string;
  theme?: Theme;
  env?: Record<string, string>;
  allowIndexing?: boolean;
  locale?: AppLocale;
}) {
  return (
    <html
      lang={locale}
      className={`${theme} h-full overflow-x-hidden`}
      suppressHydrationWarning
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta name="theme-color" content="#141A42" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="Supervity" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        {/* App ships its own locale switcher; browser translation prompts
				    would fight it and mangle already-localized copy. */}
        <meta name="google" content="notranslate" />
        {allowIndexing ? null : (
          <meta name="robots" content="noindex, nofollow" />
        )}
        {/* Align html class with cookie/localStorage before first paint so a
            stale en_theme cookie cannot flash dark→light on refresh. */}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{ __html: getThemeBootstrapScript() }}
        />
        <ClientHintCheck nonce={nonce} />
        <Links />
        <Meta />
      </head>
      <body className={`${theme} font-body!`} suppressHydrationWarning>
        {children}
        <script
          id="env-script"
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        {/* Stripe Pricing Table Script (only when Stripe is configured) */}
        {env?.STRIPE_PUBLISHABLE_KEY ? (
          <script
            async
            src="https://js.stripe.com/v3/pricing-table.js"
          ></script>
        ) : null}
        <ScrollRestoration nonce={nonce} />
        <ExternalScripts />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

/**
 * Root loader data with safe fallbacks.
 *
 * Fixes an intermittent white-screen on fresh deploys that surfaced as
 * `Cannot read properties of undefined (reading 'ALLOW_INDEXING')`:
 * `App` read `data.ENV.ALLOW_INDEXING` directly, so any render where the root
 * payload was missing or incomplete threw a TypeError *during render*. That
 * TypeError then became the error the boundary displayed, hiding whatever
 * actually went wrong (an expired session redirecting to Keycloak, a `.data`
 * request answered with HTML by the proxy mid-rollout, a stale chunk after new
 * assets shipped). The env var being set correctly never mattered — the value
 * was never reached.
 *
 * The rest of the app already treats root data as optional (`useOptionalUser`,
 * `useKeycloakClaims`); root itself was the only place that assumed it.
 *
 * ENV specifically has two independent delivery channels: the loader payload
 * and the inline `env-script` tag rendered by `Document`. They fail
 * independently, so fall back to the other one before giving up.
 */
function useRootData() {
  const data = useRouteLoaderData<typeof loader>("root");

  const env =
    data?.ENV ??
    (typeof window !== "undefined" ? window.ENV : undefined) ??
    ({} as ReturnType<typeof getEnv>);

  return {
    locale: (data?.locale ?? "en") as AppLocale,
    // Absent ENV must not flip the site to noindex — `!== "false"` keeps the
    // same default the Document component uses.
    allowIndexing: env.ALLOW_INDEXING !== "false",
    ENV: env,
    toast: data?.toast ?? null,
    requestInfo: data?.requestInfo,
    honeyProps: data?.honeyProps,
  };
}

function App() {
  const data = useRootData();
  const nonce = useNonce();
  const user = useOptionalUser();
  const location = useLocation();
  const { i18n } = useTranslation();
  useEffect(() => {
    const current = i18n.language?.split("-")[0];
    if (current === data.locale) return;
    void (async () => {
      await i18n.changeLanguage(data.locale);
      await i18n.reloadResources(data.locale);
      if (typeof document !== "undefined") {
        document.documentElement.lang = data.locale;
      }
    })();
  }, [data.locale, i18n]);
  const theme = useTheme();

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add(theme);
      document.body.classList.remove("light", "dark");
      document.body.classList.add(theme);
    }
  }, [theme]);
  const allowIndexing = data.allowIndexing;
  // Server flash toasts (getToast) render through sonner, the same toaster the
  // rest of the app calls. The Radix toast system it used to use existed only
  // for this one producer.
  useEffect(() => {
    if (!data.toast) return;
    const { type, title, description } = data.toast;
    const show =
      type === "success"
        ? toast.success
        : type === "error"
          ? toast.error
          : toast;
    if (title) show(title, { description });
    else show(description);
  }, [data.toast]);

  // TODO: to be removed once profile page user /u/profile is ready
  const profilePageMatch = useMatch("/users/:username");

  const isDocsOrTutorialsRoute =
    location.pathname.startsWith("/docs") ||
    location.pathname.startsWith("/u/alpha/tutorials");

  // Landing, pricing, login, and marketing pages use their own full-bleed layout (background + header)
  const isLandingRoute =
    !user &&
    (location.pathname === "/" ||
      location.pathname === "/login" ||
      location.pathname === "/pricing" ||
      location.pathname === "/support" ||
      location.pathname === "/privacy" ||
      location.pathname === "/tos" ||
      location.pathname === "/release-notes" ||
      location.pathname === "/changelog" ||
      location.pathname.startsWith("/marketplace"));

  // Lock full-page scroll on docs/tutorials; only the three columns scroll (run before paint)
  useLayoutEffect(() => {
    if (!isDocsOrTutorialsRoute) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    const prevBodyPos = document.body.style.position;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.inset = "0";
    document.body.style.width = "100%";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
      document.body.style.position = prevBodyPos;
      document.body.style.inset = "";
      document.body.style.width = "";
    };
  }, [isDocsOrTutorialsRoute]);

  return (
    <Document
      nonce={nonce}
      theme={theme}
      allowIndexing={allowIndexing}
      env={data.ENV}
      locale={data.locale}
    >
      <PwaManager />
      <div className="fixed left-[50%] right-0 top-0 z-1000 inline-flex w-full sm:w-1/2 translate-x-[-50%] justify-center">
        <div className="pointer-events-auto">
          <EpicToaster closeButton position="top-center" theme={theme} />
        </div>
      </div>
      {user && !profilePageMatch ? (
        <div vaul-drawer-wrapper="">
          <Outlet />
        </div>
      ) : isLandingRoute ? (
        <div vaul-drawer-wrapper="">
          <Outlet />
        </div>
      ) : isDocsOrTutorialsRoute ? (
        <div
          vaul-drawer-wrapper=""
          className="fixed inset-0 z-0 flex flex-col overflow-hidden overscroll-none"
          style={{
            height: "100dvh",
            maxHeight: "100vh",
            width: "100%",
            contain: "strict",
          }}
        >
          <Outlet />
        </div>
      ) : (
        <div vaul-drawer-wrapper="">
          <div className="flex flex-col justify-between">
            <header className="container py-6">
              <nav className="flex flex-wrap items-center justify-between gap-4 sm:flex-nowrap md:gap-8">
                <Logo />
                <div className="flex items-center gap-10">
                  {user ? (
                    <UserDropdown />
                  ) : (
                    <ProviderConnectionForm
                      type="Login"
                      providerName="oauth2"
                    ></ProviderConnectionForm>
                  )}
                </div>
              </nav>
            </header>

            <div className="flex-1">
              <Outlet />
            </div>

            {/* <div className="container flex justify-between pb-5">
							<Logo />
							<ThemeSwitch
								userPreference={data.requestInfo?.userPrefs.theme}
							/>
						</div> */}
          </div>
        </div>
      )}
      <EpicProgress />
    </Document>
  );
}

function Logo() {
  const theme = useTheme();

  return (
    <Link
      to="/"
      className="group grid auto-cols-auto grid-flow-col grid-rows-2 items-center gap-x-2 leading-snug text-slate-700 dark:text-slate-300"
    >
      <img
        className="row-span-2 size-10"
        src={
          theme === "dark"
            ? "/img/supervity-logo-dark.png"
            : "/img/supervity-logo.svg"
        }
        alt="Supervity AI Agent"
      />
      <span className="font-bold transition group-hover:-translate-x-1">
        Supervity
      </span>
      <span className="font transition group-hover:translate-x-1">
        AI Coach
      </span>
    </Link>
  );
}

function AppWithProviders() {
  // Same reasoning as useRootData: a bare `data.honeyProps` here throws before
  // App even renders, so an incomplete payload takes out the whole tree.
  const data = useRootData();
  return (
    <HoneypotProvider {...data.honeyProps}>
      <OrgTeamProvider>
        <App />
      </OrgTeamProvider>
    </HoneypotProvider>
  );
}

export default withSentry(AppWithProviders);

function UserDropdown() {
  const user = useUser();
  const submit = useSubmit();
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation("common");
  return (
    <DropdownMenu>
      {/* The <Link> is progressive enhancement: it gives the trigger a real
			    href before hydration, and the menu takes over after. Base UI
			    slots it through the Button's own `render`. */}
      <DropdownMenuTrigger
        nativeButton={false}
        render={
          <Button
            variant="secondary"
            render={
              <Link
                to={`/users/${user.username}`}
                onClick={(e) => e.preventDefault()}
                className="flex items-center gap-2"
              />
            }
          />
        }
      >
        <Avatar className="h-8 w-8">
          <AvatarImage
            src={getUserImgSrc(user.image?.id) ?? undefined}
            alt={user.name ?? user.username}
          />
          <AvatarFallback className="bg-[#5A5A5A] text-sm font-bold text-white">
            {user.username?.charAt(0)?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <span className="text-body-sm font-bold">
          {user.name ?? user.username}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent sideOffset={8} align="start">
          {/* <DropdownMenuItem render={<Link prefetch="intent" to={`/agents/new`} />}>
							<Icon name="pencil-2" className="text-body-md">
								Create AI Agent
							</Icon>
						</DropdownMenuItem> */}
          <DropdownMenuItem
            render={<Link prefetch="intent" to={`/users/${user.username}`} />}
          >
            <Icon className="text-body-md" name="avatar">
              {t("profile")}
            </Icon>
          </DropdownMenuItem>
          <DropdownMenuItem // this prevents the menu from closing before the form submission is completed
            onSelect={(event) => {
              event.preventDefault();
              clearClientAuthStateOnLogout();
              submit(formRef.current);
            }}
            render={
              <Form
                action="/logout"
                method="POST"
                ref={formRef}
                onSubmit={() => {
                  clearClientAuthStateOnLogout();
                }}
              />
            }
          >
            <Icon className="text-body-md" name="exit">
              <button type="submit">{t("logout")}</button>
            </Icon>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
      <GeneralErrorBoundary />
    </Document>
  );
}
